import React, { useMemo, useRef, useState } from "react";
import { PayloadAction } from "@reduxjs/toolkit";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import {
  AirSavedCardModel,
  airSavedCardStore,
  EditAirTagsFromCardsFunc,
  GetAirCardsFunc,
  isAirSavedCardModel,
} from "entities/AirSavedCard";
import { searchFilterStore } from "entities/SearchFilter";
import {
  EditSeaTagsFromCardsFunc,
  GetSeaCardsFunc,
  isSeaSavedShipmentModel,
  SeaSavedCardModel,
  seaSavedCardStore,
  UpdateCardQuery,
} from "entities/SeaSavedCard";
import { useDebounce } from "shared/hooks/useDebounce.hook";
import { useResize } from "shared/hooks/useResize.hook";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { Button } from "shared/ui/Button";
import { CustomScrollBar } from "shared/ui/CustomScrollBar";
import { Loader } from "shared/ui/Loader";
import styles from "./EditTagsForm.module.scss";

import { calcWidthInput } from "../EditTags.utils";
import { InputAddTag } from "../InputAddTag";
import { TagList } from "../TagList";
import { TagSuggestion } from "../TagSuggestion";

type EditTagsFormProps = {
  closePopup: () => void;
  editTagsFromCards: EditSeaTagsFromCardsFunc | EditAirTagsFromCardsFunc;
  isTagsProcessing: boolean;
  tags: Array<string>;
  allTags: Array<string>;
  cardId: UpdateCardQuery["id"];
  setNewTags: (tags: string[]) => void;
  getCardsByTags: GetSeaCardsFunc | GetAirCardsFunc;
  activeTag: string | null;
  setActiveTag: (tag: string | null) => void;
  refreshTags: () => void;
  cardsByTag: SeaSavedCardModel[] | AirSavedCardModel[];
  setCardsByTagAction: (
    cards: SeaSavedCardModel[] | AirSavedCardModel[]
  ) => PayloadAction<SeaSavedCardModel[] | AirSavedCardModel[]>;
};

type Tag = string;

export const EditTagsForm: RFC<EditTagsFormProps> = ({
  tags: tagsProps,
  allTags,
  cardId,
  closePopup,
  setNewTags,
  editTagsFromCards,
  isTagsProcessing,
  getCardsByTags,
  activeTag,
  setActiveTag,
  refreshTags,
  cardsByTag,
  setCardsByTagAction,
}) => {
  const { t } = useTranslation();
  const { debounce } = useDebounce(100);
  const dispatch = useAppDispatch();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const seaSavedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const airSavedCards = useAppSelector(airSavedCardStore.selectors.savedCards);

  const inputErrorText = `${t("The tag name can only include Latin letters, numbers and dashes")}.`;

  const savedCards = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaSavedCards;
      case ShipmentType.AIR:
        return airSavedCards;
    }
  }, [shipment, seaSavedCards, airSavedCards]);

  const [tags, setTags] = useState([...tagsProps]);
  const [searchTag, setSearchTag] = useState("");

  const [isError, setError] = useState(false);
  const [isRepeat, setRepeat] = useState(false);

  const updateTags = (newTagsList: Tag[]) => {
    setTags(newTagsList);
    setNewTags(newTagsList);
  };

  const addTag = (tag: Tag) => {
    const tagsWithNewTag = [...tags, tag];
    updateTags(tagsWithNewTag);
  };

  const deleteTag = (tag: Tag) => {
    const tagsWithoutSelectedTag = tags.filter((item) => item !== tag);
    updateTags(tagsWithoutSelectedTag);
  };

  const checkRepeatTag = (tag: Tag) => tags.indexOf(tag) !== -1;

  const tagsSuggestion = useMemo(() => {
    const search = searchTag.toLowerCase();
    return allTags.filter((tag) => {
      if (typeof tag !== "string" || !tag) return false;
      return tags.indexOf(tag) === -1 && tag.toLowerCase().indexOf(search) !== -1;
    });
  }, [allTags, tags, searchTag]);

  const inputRef = useRef<HTMLInputElement>(null) as React.MutableRefObject<HTMLInputElement>;

  const [isOpen, setOpen] = useState(false);

  const onFocus = () => {
    setOpen(true);
  };

  const onBlur = () => {
    setOpen(false);
  };

  const onMouseDownTags = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const onClickTags = () => {
    inputRef.current.focus();
  };

  const sendTags = () => {
    editTagsFromCards(
      { id: cardId, tagsList: tags },
      {
        onSettled: (response) => {
          const isSavedCard = savedCards?.find((card) => card.id === cardId);
          if (response && isSavedCard) {
            switch (shipment) {
              case ShipmentType.SEA:
                if (isSeaSavedShipmentModel(response)) {
                  dispatch(seaSavedCardStore.actions.updateSavedCardAction(response as SeaSavedCardModel));
                }
                break;
              case ShipmentType.AIR:
                if (isAirSavedCardModel(response)) {
                  dispatch(airSavedCardStore.actions.updateSavedCardAction(response as AirSavedCardModel));
                }
                break;
            }
          }
          closePopup();
          refreshTags();

          if (!activeTag) return;

          getCardsByTags(
            {
              where: { tags: [activeTag] },
              limit: cardsByTag.length,
            },
            {
              onSuccess: (response) => {
                if (!response.length) {
                  setActiveTag(null);
                }
                dispatch(setCardsByTagAction(response));
              },
            }
          );
        },
      }
    );
  };

  useResize(() =>
    debounce(() => {
      calcWidthInput(inputRef.current, inputRef.current);
    })
  );

  const containerClasses = cn(styles.inputContainer, { [styles.tagsActive]: isOpen });

  return (
    <div className={styles.tagsModal}>
      <div className={styles.titleModal}>{t("Tags")}</div>
      <div className={containerClasses}>
        <CustomScrollBar>
          <div className={styles.tags} onMouseDown={onMouseDownTags} onClick={onClickTags}>
            <TagList tags={tags} deleteTag={deleteTag} />
            <InputAddTag
              onFocus={onFocus}
              onBlur={onBlur}
              ref={inputRef}
              addTag={addTag}
              checkRepeatTag={checkRepeatTag}
              setError={setError}
              setRepeat={setRepeat}
              setSearchTag={setSearchTag}
            />
          </div>
        </CustomScrollBar>
        {isOpen && !isError && !isRepeat && !!tagsSuggestion.length && (
          <TagSuggestion
            inputRef={inputRef}
            tagsSuggestion={tagsSuggestion}
            addTag={addTag}
            setSearchTag={setSearchTag}
          />
        )}
      </div>
      {isError && <div className={styles.validationMessage}>{inputErrorText}</div>}
      {isRepeat && <div className={styles.validationMessage}>{t("Repeat")}</div>}

      <div className={styles.btnContainer}>
        <div className={styles.btnCancel}>
          <Button
            disabled={isTagsProcessing}
            variant="secondary"
            size="small"
            onClick={() => {
              if (!isTagsProcessing) {
                closePopup();
              }
            }}
          >
            {t("Cancel")}
          </Button>
        </div>
        <div className={styles.btnSave}>
          <Button
            variant="primary"
            size="small"
            onClick={() => {
              sendTags();
            }}
            disabled={isTagsProcessing}
          >
            {t("Save")} {isTagsProcessing && <Loader size="20px" className={styles.deleteButtonLoader} />}
          </Button>
        </div>
      </div>
    </div>
  );
};
